import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, XCircle, MinusCircle, ChevronDown, ChevronUp } from "lucide-react";
import { API, errMsg, fmtTime, TOPIC_NAMES } from "../lib/api";
import { PageHeader, StatCard } from "../components/bits";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const LETTERS = ["A", "B", "C", "D", "E"];

function ReviewItem({ item, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const q = item.question;
  return (
    <div className="border border-border rounded-lg bg-card" data-testid={`result-item-${q.qid}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left px-5 py-4 flex items-start gap-3">
        {item.correct === null ? <MinusCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
          : item.correct ? <CheckCircle2 className="w-5 h-5 text-success shrink-0 mt-0.5" />
          : <XCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />}
        <div className="flex-1">
          <p className="text-sm font-medium leading-relaxed line-clamp-2">{q.stem}</p>
          <p className="text-xs text-muted-foreground mt-1">{q.qid} · {TOPIC_NAMES[q.topic] || q.topic} · {q.difficulty}</p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-border pt-4">
          <div className="space-y-2">
            {LETTERS.map((l) => {
              let cls = "text-muted-foreground";
              if (l === q.correct_answer) cls = "text-success font-semibold";
              else if (l === item.selected) cls = "text-destructive font-semibold";
              return (
                <p key={l} className={`text-sm ${cls}`} data-testid={`result-option-${q.qid}-${l}`}>
                  {l}. {q.options[l]}
                  {l === q.correct_answer && " ✓ correct"}
                  {l === item.selected && l !== q.correct_answer && " ✗ your answer"}
                </p>
              );
            })}
          </div>
          <div className="bg-background border border-border rounded-md p-4">
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-1.5">Why {q.correct_answer} is correct</p>
            <p className="text-sm leading-relaxed">{q.explanation_correct}</p>
          </div>
          <div className="bg-background border border-border rounded-md p-4 space-y-2">
            {LETTERS.filter((l) => l !== q.correct_answer).map((l) => (
              <p key={l} className="text-sm leading-relaxed">
                <Yspan className="font-semibold">{l}:</Yspan>{" "}
                <span className="text-muted-foreground">{q.option_explanations?.[l]}</span>
              </p>
            ))}
          </div>
          {q.key_learning_point && (
            <p className="text-sm bg-secondary border border-border rounded-md p-4">
              <span className="font-semibold">Key point: </span>{q.key_learning_point}
              {q.reference && <span className="block mt-1.5 text-xs text-muted-foreground">Reference: {q.reference}</span>}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

const Yspan = ({ className, children }) => <span className={className}>{children}</span>;

export default function Results() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    API.get(`/sessions/${id}/results`).then(({ data }) => setData(data))
      .catch((e) => { toast.error(errMsg(e)); navigate("/app"); });
  }, [id, navigate]);

  if (!data) return <div className="py-20 text-center text-muted-foreground text-sm" data-testid="results-loading">Calculating your results…</div>;

  const items = data.items.filter((it) => filter === "all" ? true : filter === "incorrect" ? it.correct === false : it.correct === true);

  return (
    <div data-testid="results-page" className="max-w-4xl">
      <PageHeader title="Your results" sub={`${data.mode === "test" ? "Test" : "Quiz"} · ${data.total} questions`} />

      <div className="bg-primary text-primary-foreground rounded-lg p-8 md:p-10 flex flex-wrap items-center gap-10" data-testid="score-hero">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary-foreground/60">Score</p>
          <p className="font-display text-5xl md:text-6xl font-extrabold mt-2" data-testid="score-fraction">{data.correct} / {data.answered}</p>
        </div>
        <div>
          <p className="font-display text-4xl font-bold" data-testid="score-percentage">{data.percentage}%</p>
          <p className="text-sm text-primary-foreground/70 mt-1">
            {data.percentage >= 80 ? "Excellent — PARA-ready performance." : data.percentage >= 60 ? "Solid — keep building." : "Focus on the explanations below."}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <StatCard label="Correct" value={data.correct} testid="results-correct" />
        <StatCard label="Incorrect" value={data.incorrect} testid="results-incorrect" />
        <StatCard label="Time taken" value={fmtTime(data.total_time_ms)} testid="results-time" />
        <StatCard label="Avg per question" value={fmtTime(data.avg_time_ms)} testid="results-avg-time" />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <div className="bg-card border border-border rounded-lg p-6" data-testid="difficulty-breakdown">
          <h3 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-4">By difficulty</h3>
          {["easy", "medium", "hard"].map((d) => {
            const b = data.by_difficulty[d];
            if (!b || !b.answered) return null;
            const pct = Math.round((b.correct / b.answered) * 100);
            return (
              <div key={d} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <span className="text-sm font-medium capitalize">{d}</span>
                <span className={`text-sm font-bold ${pct >= 70 ? "text-success" : "text-destructive"}`} data-testid={`difficulty-score-${d}`}>
                  {b.correct}/{b.answered} · {pct}%
                </span>
              </div>
            );
          })}
        </div>
        <div className="bg-card border border-border rounded-lg p-6" data-testid="topic-breakdown">
          <h3 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-4">By topic</h3>
          {Object.entries(data.by_topic).map(([t, b]) => {
            if (!b.answered) return null;
            const pct = Math.round((b.correct / b.answered) * 100);
            return (
              <div key={t} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <span className="text-sm font-medium">{TOPIC_NAMES[t] || t}</span>
                <span className={`text-sm font-bold ${pct >= 70 ? "text-success" : "text-destructive"}`}>{b.correct}/{b.answered} · {pct}%</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mt-8">
        <Button variant={filter === "incorrect" ? "default" : "outline"} data-testid="review-incorrect-btn" onClick={() => setFilter("incorrect")}>
          Review incorrect ({data.incorrect})
        </Button>
        <Button variant={filter === "all" ? "default" : "outline"} data-testid="review-all-btn" onClick={() => setFilter("all")}>
          Review all questions
        </Button>
        <Button variant="outline" data-testid="try-another-btn" onClick={() => navigate("/app/topics")}>
          Try another session
        </Button>
      </div>

      <div className="mt-6 space-y-4" data-testid="results-review-list">
        {items.map((item) => <ReviewItem key={item.question.id} item={item} defaultOpen={filter === "incorrect"} />)}
        {items.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">Nothing to show for this filter.</p>}
      </div>
    </div>
  );
}
